import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, Loader2, AlertTriangle, RotateCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AccountShell } from "../../components/account/AccountShell";
import { usePro } from "../../hooks/usePro";
import { useAuth } from "../../hooks/useAuth";
import { getSubscription, cancelSubscription, resumeSubscription } from "../../lib/api";
import { fmtDate, fmtMoney } from "../../lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-600",
  expired: "bg-gray-100 text-gray-500",
  cancelled: "bg-red-50 text-red-600",
};

const LOSE_ACCESS = ["Build Journey", "Premium Business Ideas", "AI Builder Prompts", "Saved Progress"];

export default function AccountPlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkout, checkingOut, openUpgrade } = usePro();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getSubscription()
      .then(setData)
      .catch(() => setData({ subscription: null, plans: [] }))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const sub = data?.subscription;
  const cancelling = sub?.cancel_at_period_end;
  const statusLabel = sub
    ? sub.status === "active"
      ? cancelling
        ? `Cancels ${fmtDate(sub.current_period_end)}`
        : "Active"
      : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)
    : "No plan";

  const doCancel = async () => {
    setBusy(true);
    try {
      await cancelSubscription();
      toast.success("Subscription cancelled", { description: `Access stays active until ${fmtDate(sub.current_period_end)}.` });
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel subscription.");
    } finally {
      setBusy(false);
    }
  };

  const doResume = async () => {
    setBusy(true);
    try {
      await resumeSubscription();
      toast.success("Subscription resumed", { description: "Your plan will keep renewing." });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resume subscription.");
    } finally {
      setBusy(false);
    }
  };

  const changePlan = (plan) => checkout(plan);

  return (
    <AccountShell
      title="Plans & Subscription"
      subtitle="Manage your LaunchBS plan, renewal and billing."
      testid="account-plans-page"
    >
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#687280]" data-testid="plans-loading">
          <Loader2 className="animate-spin" size={22} />
        </div>
      ) : sub && (sub.status === "active" || sub.status === "cancelled") ? (
        <>
          {/* Current subscription card */}
          <div className="bg-[#0F0F0F] rounded-3xl p-8 md:p-10 text-white" data-testid="current-subscription-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] px-3 py-1 text-[11px] font-semibold mb-4">
                  <Crown size={13} /> CURRENT SUBSCRIPTION
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold">{sub.plan_label}</h2>
                <p className="text-3xl font-display font-extrabold mt-3">
                  {fmtMoney(sub.amount, sub.currency?.toUpperCase() || "USD")}
                  <span className="text-base font-medium text-[#A1A1AA]">/{sub.interval}</span>
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold ${cancelling ? "bg-amber-400/15 text-amber-300" : STATUS_BADGE[sub.status] || "bg-white/10 text-white"}`}
                data-testid="subscription-status-badge"
              >
                {statusLabel}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 pt-8 border-t border-white/10">
              <div>
                <p className="text-xs text-[#687280]">{cancelling ? "Service ends on" : "Renews on"}</p>
                <p className="font-semibold mt-1">{fmtDate(sub.current_period_end)}</p>
              </div>
              <div>
                <p className="text-xs text-[#687280]">Charged in</p>
                <p className="font-semibold mt-1">{(sub.currency || "usd").toUpperCase()}</p>
              </div>
            </div>

            {cancelling && (
              <div className="mt-6 flex items-start gap-2.5 rounded-xl bg-amber-400/10 border border-amber-400/20 p-4 text-sm text-amber-200" data-testid="cancel-notice">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>Your subscription is set to cancel. Your service will end on {fmtDate(sub.current_period_end)}.</span>
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-8">
              <button
                onClick={() => navigate("/account/plans/details")}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold hover:border-[#D4AF37] transition-colors"
                data-testid="view-plan-details-btn"
              >
                View Plan Details
              </button>
              {!cancelling && (
                <button
                  onClick={() => changePlan(sub.plan === "monthly" ? "annual" : "monthly")}
                  disabled={checkingOut}
                  className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#0F0F0F] px-5 py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
                  data-testid="change-plan-btn"
                >
                  {checkingOut ? <Loader2 className="animate-spin" size={15} /> : `Switch to ${sub.plan === "monthly" ? "Annual" : "Monthly"}`}
                </button>
              )}
              {cancelling ? (
                <button
                  onClick={doResume}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F0F0F] px-5 py-2.5 text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-60"
                  data-testid="resume-subscription-btn"
                >
                  {busy ? <Loader2 className="animate-spin" size={15} /> : <><RotateCcw size={15} /> Resume Subscription</>}
                </button>
              ) : (
                <button
                  onClick={() => setConfirmOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-red-400/30 text-red-300 px-5 py-2.5 text-sm font-semibold hover:bg-red-500/10 transition-colors"
                  data-testid="cancel-subscription-btn"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>

          {/* Plan options */}
          {cancelling && (
            <p className="mt-8 -mb-2 text-sm text-[#687280]" data-testid="resubscribe-hint">
              Changed your mind? Resume your plan or switch to the other one below.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
            {data.plans.map((p) => {
              const current = p.plan === sub.plan;
              return (
                <div
                  key={p.plan}
                  className={`rounded-2xl p-6 border ${current ? "border-[#D4AF37] bg-[#FBF7EA]" : "border-gray-100 bg-white"} shadow-[0_8px_30px_rgb(0,0,0,0.04)]`}
                  data-testid={`plan-option-${p.plan}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-[#0F0F0F]">{p.label}</h3>
                    {current && <span className="text-[11px] font-semibold text-[#8a6d1a]">CURRENT</span>}
                  </div>
                  <p className="text-2xl font-display font-extrabold text-[#0F0F0F] mt-2">
                    {fmtMoney(p.amount)}<span className="text-sm font-medium text-[#687280]">/{p.interval}</span>
                  </p>
                  {cancelling ? (
                    current ? (
                      <button
                        onClick={doResume}
                        disabled={busy}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors disabled:opacity-60"
                        data-testid={`resume-plan-${p.plan}`}
                      >
                        {busy ? <Loader2 className="animate-spin" size={15} /> : "Resume this plan"}
                      </button>
                    ) : (
                      <button
                        onClick={() => changePlan(p.plan)}
                        disabled={checkingOut}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
                        data-testid={`select-plan-${p.plan}`}
                      >
                        {checkingOut ? <Loader2 className="animate-spin" size={15} /> : `Switch to ${p.interval === "year" ? "Annual" : "Monthly"}`}
                      </button>
                    )
                  ) : (
                    !current && (
                      <button
                        onClick={() => changePlan(p.plan)}
                        disabled={checkingOut}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors disabled:opacity-60"
                        data-testid={`select-plan-${p.plan}`}
                      >
                        Switch to this plan
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        /* No active subscription */
        <div className="bg-[#0F0F0F] rounded-3xl p-10 text-center" data-testid="no-subscription-card">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 text-[#D4AF37] px-4 py-1.5 text-xs font-semibold mb-4">
            <Crown size={14} /> LAUNCHBS PRO
          </div>
          <h2 className="font-display text-2xl font-bold text-white">
            {sub?.status === "expired" ? "Your subscription has expired" : "You don't have an active plan"}
          </h2>
          <p className="text-[#A1A1AA] mt-2 max-w-md mx-auto">
            Unlock unlimited saves, notes, AI build prompts and premium ideas.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-sm text-[#A1A1AA]">
            {["Unlimited saves", "Startup Notebook", "AI build prompts", "Priority support"].map((f) => (
              <span key={f} className="inline-flex items-center gap-2">
                <Check size={14} className="text-[#D4AF37]" /> {f}
              </span>
            ))}
          </div>
          {data?.plans?.length ? (
            <div className="flex flex-col sm:flex-row justify-center gap-3 mt-7" data-testid="resubscribe-options">
              {data.plans.map((p) => (
                <button
                  key={p.plan}
                  onClick={() => changePlan(p.plan)}
                  disabled={checkingOut}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-colors disabled:opacity-60 ${p.interval === "year" ? "bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030]" : "bg-white text-[#0F0F0F] hover:bg-gray-100"}`}
                  data-testid={`resubscribe-${p.plan}`}
                >
                  {checkingOut ? <Loader2 className="animate-spin" size={16} /> : <><Crown size={16} /> {p.interval === "year" ? "Go Annual" : "Go Monthly"} · {fmtMoney(p.amount)}/{p.interval}</>}
                </button>
              ))}
            </div>
          ) : (
            <button
              onClick={() => openUpgrade("default")}
              className="mt-7 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors"
              data-testid="subscribe-btn"
            >
              <Crown size={18} /> Upgrade to LaunchBS Pro <ArrowRight size={16} />
            </button>
          )}
          <p className="text-xs text-[#687280] mt-3">$9.99/month or $99/year · Save 17%</p>
        </div>
      )}

      {/* Cancel confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="cancel-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Are you sure?</DialogTitle>
            <DialogDescription>
              Your subscription will remain active until <strong>{fmtDate(sub?.current_period_end)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-[#F7F7F7] p-4">
            <p className="text-sm font-semibold text-[#0F0F0F] mb-2">After this date you will lose access to:</p>
            <ul className="space-y-1.5">
              {LOSE_ACCESS.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[#687280]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 mt-2">
            <button
              onClick={doCancel}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-red-200 text-red-600 py-3 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-60"
              data-testid="confirm-cancel-btn"
            >
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Cancel Subscription"}
            </button>
            <button
              onClick={() => setConfirmOpen(false)}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
              data-testid="keep-subscription-btn"
            >
              Keep Subscription
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AccountShell>
  );
}
